'use client';
import React from 'react';
import styles from './ContentfulTextEditor.module.css';
import { ContentfulTextEditorPropTypes } from './ContentfulTextEditor.types';

function hasTable(node: React.ReactNode): boolean {
  if (node == null || typeof node === 'boolean' || typeof node === 'number') return false;

  if (typeof node === 'string') {
    // Catch HTML strings rendered via dangerouslySetInnerHTML upstream
    return node.toLowerCase().includes('<table');
  }

  if (Array.isArray(node)) return node.some(hasTable);

  if (React.isValidElement<Record<string, any>>(node)) {
    // Direct table element
    if (node.type === 'table') return true;

    // Tables embedded via dangerouslySetInnerHTML on this node
    const html = (node.props?.dangerouslySetInnerHTML?.__html as string | undefined)?.toLowerCase();
    if (html?.includes('<table')) return true;

    // Recurse into children
    return hasTable(node.props?.children);
  }

  return false;
}

// Strip the inline `style="..."` attribute off any <table ...> opening tag in an
// HTML string. TinyMCE bakes fixed widths onto the table tag which break the
// mobile layout; removing it lets our own responsive table styles take over.
function stripTableStyleFromHtml(html: string): string {
  return html.replace(/(<table\b[^>]*?)\s+style\s*=\s*("[^"]*"|'[^']*')/gi, '$1');
}

// Return a copy of the node tree with inline styles removed from every table,
// whether the table is a real React element or embedded as an HTML string via
// dangerouslySetInnerHTML. Traversal mirrors hasTable().
function stripTableStyles(node: React.ReactNode): React.ReactNode {
  if (node == null || typeof node === 'boolean' || typeof node === 'number') return node;

  if (typeof node === 'string') {
    return node.toLowerCase().includes('<table') ? stripTableStyleFromHtml(node) : node;
  }

  if (Array.isArray(node)) return node.map((child) => stripTableStyles(child));

  if (React.isValidElement<Record<string, any>>(node)) {
    const props = node.props ?? {};
    const nextProps: Record<string, any> = {};

    // Real <table> element carrying an inline style prop → drop it.
    if (node.type === 'table' && props.style) {
      nextProps.style = undefined;
    }

    // Tables embedded via dangerouslySetInnerHTML on this node.
    const html = props.dangerouslySetInnerHTML?.__html as string | undefined;
    if (html?.toLowerCase().includes('<table')) {
      nextProps.dangerouslySetInnerHTML = { __html: stripTableStyleFromHtml(html) };
    } else if (props.children != null) {
      // dangerouslySetInnerHTML owns its subtree, so only recurse otherwise.
      const stripped = stripTableStyles(props.children);
      if (stripped !== props.children) nextProps.children = stripped;
    }

    return Object.keys(nextProps).length ? React.cloneElement(node, nextProps) : node;
  }

  return node;
}

const ContentfulTextEditor: React.FC<ContentfulTextEditorPropTypes> = ({ className = '', children }) => {
  const outerRef = React.useRef<HTMLDivElement | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const fadeRightRef = React.useRef<HTMLDivElement | null>(null);
  const fadeLeftRef = React.useRef<HTMLDivElement | null>(null);

  const shouldWrap = React.useMemo(() => hasTable(children), [children]);

  // TinyMCE adds unwanted inline styles to table tags; strip them so they don't
  // override the responsive table styling (only relevant when a table exists).
  const cleanedChildren = React.useMemo(
    () => (shouldWrap ? stripTableStyles(children) : children),
    [shouldWrap, children],
  );

  // Gradient visibility + scroll handling
  React.useEffect(() => {
    if (!shouldWrap) return;

    const scroller = scrollRef.current;
    const fadeR = fadeRightRef.current;
    const fadeL = fadeLeftRef.current;
    if (!scroller || !fadeR || !fadeL) return;

    const updateFades = () => {
      const { scrollWidth, clientWidth, scrollLeft } = scroller;
      const atStart = scrollLeft <= 0;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1; // -1 for float rounding

      // Drive opacity (not display) so the fades ease in/out via the CSS transition.
      fadeR.style.opacity = atEnd ? '0' : '1';
      fadeL.style.opacity = atStart ? '0' : '1';
    };

    // Initial state, resize, and on scroll
    updateFades();
    scroller.addEventListener('scroll', updateFades, { passive: true });
    window.addEventListener('resize', updateFades);

    // The table's real width often isn't known on first paint (layout/fonts still
    // settling), so the initial measurement can wrongly read as "no overflow" and
    // hide the right fade until the first scroll. Re-measure once the content is
    // laid out and whenever the scroller's size changes.
    const raf = requestAnimationFrame(updateFades);
    const resizeObserver = new ResizeObserver(updateFades);
    resizeObserver.observe(scroller);

    return () => {
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      scroller.removeEventListener('scroll', updateFades);
      window.removeEventListener('resize', updateFades);
    };
  }, [shouldWrap]);

  // Ensure autoplay videos play inline (within our rendered content only)
  React.useEffect(() => {
    const root = outerRef.current;
    if (!root) return;
    root.querySelectorAll<HTMLVideoElement>('video[autoplay]').forEach((v) => v.setAttribute('playsinline', ''));
  }, [children]);

  return (
    <div ref={outerRef} className={`${styles?.contentfulTextEditor} ${className}`}>
      {shouldWrap ? (
        <div style={{ position: 'relative' }}>
          <div className='scroll-indicator' ref={scrollRef} tabIndex={0}>
            {cleanedChildren}
          </div>
          <div className='gradient-fade' ref={fadeRightRef} aria-hidden='true'>
            &nbsp;
          </div>
          <div className='gradient-fade-left' ref={fadeLeftRef} aria-hidden='true'>
            &nbsp;
          </div>
        </div>
      ) : (
        children
      )}
    </div>
  );
};

export default ContentfulTextEditor;
